import { useState } from "react";
import {
  runMlPremium,
  runMlMinPrice,
  runAmzPrice,
  runAmzStock,
  runShopeeAttrs,
  runSyncImages,
  runMlClose,
  runNvsCategory,
  runNvsSubcategories,
  generateNvsDescription,
} from "../api/client";
import JobLog from "../components/JobLog";

// ─── Campo de escopo reutilizável ─────────────────────────────────────────────

function ScopeFields({ scope, setScope, sku, setSku, skuFile, setSkuFile }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Escopo</label>
        <div className="flex gap-5 flex-wrap">
          {[
            { val: "sku",      label: "SKU único" },
            { val: "sku_file", label: "Arquivo de SKUs" },
            { val: "all",      label: "Todos" },
          ].map(({ val, label }) => (
            <label key={val} className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="radio"
                name={`scope-${label}`}
                checked={scope === val}
                onChange={() => setScope(val)}
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      {scope === "sku" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
          <input
            type="text"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            placeholder="EG-FK-XXXX"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      {scope === "sku_file" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Caminho do arquivo</label>
          <input
            type="text"
            value={skuFile}
            onChange={(e) => setSkuFile(e.target.value)}
            placeholder="data/entrada/skus.txt"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}
    </div>
  );
}

function buildScopeBody(scope, sku, skuFile) {
  if (scope === "sku")      return { sku: sku.trim() };
  if (scope === "sku_file") return { sku_file: skuFile.trim() };
  return { all: true };
}

// ─── ML Premium ───────────────────────────────────────────────────────────────

function MlPremiumForm() {
  const [scope, setScope]       = useState("sku");
  const [sku, setSku]           = useState("");
  const [skuFile, setSkuFile]   = useState("");
  const [dryRun, setDryRun]     = useState(false);
  const [debug, setDebug]       = useState(false);
  const [listingType, setListingType] = useState("premium");
  const [jobId, setJobId]       = useState(null);
  const [running, setRunning]   = useState(false);
  const [error, setError]       = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setRunning(true); setJobId(null);
    try {
      const { data } = await runMlPremium({ ...buildScopeBody(scope, sku, skuFile), dry_run: dryRun, debug, listing_type: listingType });
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Cria o anúncio que falta no ML a partir do que o SKU já tem: <strong>Premium</strong> nasce do
        Clássico e <strong>Clássico</strong> nasce do Premium (preço da coluna ML da planilha, igual pros dois).
        A conta (principal ou SP-ML) é escolhida pelo SKU. Escolhe automaticamente a estratégia: <code className="bg-gray-100 px-1 rounded">post_catalog_family</code>,{" "}
        <code className="bg-gray-100 px-1 rounded">post_catalog</code> ou{" "}
        <code className="bg-gray-100 px-1 rounded">post_family</code>.
      </p>

      <ScopeFields {...{ scope, setScope, sku, setSku, skuFile, setSkuFile }} />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de anúncio a criar</label>
        <div className="flex gap-5">
          {[{ val: "premium", label: "Premium" }, { val: "classic", label: "Clássico" }].map(({ val, label }) => (
            <label key={val} className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input type="radio" name="ml-listing-type" checked={listingType === val} onChange={() => setListingType(val)} />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-5 flex-wrap">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="rounded" />
          Dry-run (simular sem criar)
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={debug} onChange={(e) => setDebug(e.target.checked)} className="rounded" />
          Debug (mostrar estratégia e source)
        </label>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        {dryRun ? "Simular" : "Executar"}
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

// ─── ML Preço Mínimo ──────────────────────────────────────────────────────────

function MlMinPriceForm() {
  const [scope, setScope]     = useState("sku");
  const [sku, setSku]         = useState("");
  const [skuFile, setSkuFile] = useState("");
  const [dryRun, setDryRun]   = useState(false);
  const [jobId, setJobId]     = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setRunning(true); setJobId(null);
    try {
      const { data } = await runMlMinPrice({ ...buildScopeBody(scope, sku, skuFile), dry_run: dryRun });
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Atualiza o preço mínimo dos anúncios Clássico e Premium com base no valor ML da planilha (mesmo preço para os dois).
      </p>

      <ScopeFields {...{ scope, setScope, sku, setSku, skuFile, setSkuFile }} />

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="rounded" />
        Dry-run (simular sem enviar)
      </label>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        {dryRun ? "Simular" : "Executar"}
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

// ─── Amazon Preço ─────────────────────────────────────────────────────────────

function AmzPriceForm() {
  const [scope, setScope]     = useState("sku");
  const [sku, setSku]         = useState("");
  const [skuFile, setSkuFile] = useState("");
  const [dryRun, setDryRun]   = useState(false);
  const [force, setForce]     = useState(false);
  const [jobId, setJobId]     = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setRunning(true); setJobId(null);
    try {
      const { data } = await runAmzPrice({ ...buildScopeBody(scope, sku, skuFile), dry_run: dryRun, force });
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Envia <code className="bg-gray-100 px-1 rounded">AMZ_Preço Venda</code> como{" "}
        <code className="bg-gray-100 px-1 rounded">our_price</code> e{" "}
        <code className="bg-gray-100 px-1 rounded">AMZ_Preço Mínimo</code> como guardrail de precificação automática.
        Associa à regra <em>Competitive Buy Box</em>.
      </p>

      <ScopeFields {...{ scope, setScope, sku, setSku, skuFile, setSkuFile }} />

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="rounded" />
        Dry-run (simular sem enviar)
      </label>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="rounded" />
        Forçar atualização (reenvia mesmo se preço/automação já estiverem corretos)
      </label>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        {dryRun ? "Simular" : "Executar"}
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

// ─── Amazon Estoque ───────────────────────────────────────────────────────────

function AmzStockForm() {
  const [scope, setScope]     = useState("sku");
  const [sku, setSku]         = useState("");
  const [skuFile, setSkuFile] = useState("");
  const [dryRun, setDryRun]   = useState(false);
  const [jobId, setJobId]     = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setRunning(true); setJobId(null);
    try {
      const { data } = await runAmzStock({ ...buildScopeBody(scope, sku, skuFile), dry_run: dryRun });
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Ajusta a quantidade dos anúncios <strong>FBM</strong> pro estoque geral do Bling (total menos o
        depósito Amazon FBA) — o mesmo valor que o GVC confere. Anúncios FBA são pulados (a Amazon controla).
      </p>

      <ScopeFields {...{ scope, setScope, sku, setSku, skuFile, setSkuFile }} />

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="rounded" />
        Dry-run (simular sem enviar)
      </label>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        {dryRun ? "Simular" : "Executar"}
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

// ─── Shopee Atributos ─────────────────────────────────────────────────────────

function ShopeeAttrsForm() {
  const [scope, setScope]             = useState("sku");
  const [sku, setSku]                 = useState("");
  const [skuFile, setSkuFile]         = useState("");
  const [dryRun, setDryRun]           = useState(false);
  const [noUpdateIfExists, setNoUpdate] = useState(false);
  const [fixImageOrder, setFixImage]  = useState(false);
  const [jobId, setJobId]             = useState(null);
  const [running, setRunning]         = useState(false);
  const [error, setError]             = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setRunning(true); setJobId(null);
    try {
      const { data } = await runShopeeAttrs({
        ...buildScopeBody(scope, sku, skuFile),
        dry_run: dryRun,
        no_update_if_exists: noUpdateIfExists,
        fix_image_order: fixImageOrder,
      });
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Define os 3 atributos padrão Funko Pop na Shopee: Material (PVC), Garantia (Fabricante) e Quantidade (1).
      </p>

      <ScopeFields {...{ scope, setScope, sku, setSku, skuFile, setSkuFile }} />

      <div className="flex gap-5 flex-wrap">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="rounded" />
          Dry-run
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={noUpdateIfExists} onChange={(e) => setNoUpdate(e.target.checked)} className="rounded" />
          Não atualizar se atributos já existem
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={fixImageOrder} onChange={(e) => setFixImage(e.target.checked)} className="rounded" />
          Corrigir ordem das imagens (ref. Bling)
        </label>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        {dryRun ? "Simular" : "Executar"}
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

// ─── Sync Imagens ─────────────────────────────────────────────────────────────

function SyncImagesForm() {
  const [scope, setScope]       = useState("sku");
  const [sku, setSku]           = useState("");
  const [skuFile, setSkuFile]   = useState("");
  const [dryRun, setDryRun]     = useState(false);
  const [forceImages, setForce] = useState(false);
  const [jobId, setJobId]       = useState(null);
  const [running, setRunning]   = useState(false);
  const [error, setError]       = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setRunning(true); setJobId(null);
    try {
      const { data } = await runSyncImages({
        ...buildScopeBody(scope, sku, skuFile),
        dry_run: dryRun,
        force_images: forceImages,
      });
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Sincroniza imagens ML → OCI → Bling + Shopee. Quando ML tem ≤1 imagem, busca fallback na Amazon.
        Limite de 9 imagens por produto. A imagem promocional é sempre adicionada por último.
      </p>

      <ScopeFields {...{ scope, setScope, sku, setSku, skuFile, setSkuFile }} />

      <div className="flex gap-5 flex-wrap">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="rounded" />
          Dry-run
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={forceImages} onChange={(e) => setForce(e.target.checked)} className="rounded" />
          Forçar re-download e re-upload (ignora cache OCI)
        </label>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        {dryRun ? "Simular" : "Executar"}
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

// ─── Fechar Anúncios MLP ──────────────────────────────────────────────────────

function MlCloseForm() {
  const [sku, setSku]         = useState("");
  const [confirm, setConfirm] = useState(false);
  const [excluir, setExcluir] = useState(false);
  const [jobId, setJobId]     = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setRunning(true); setJobId(null);
    try {
      const body = { confirm, excluir };
      if (sku.trim()) body.sku = sku.trim();
      const { data } = await runMlClose(body);
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3 text-sm text-yellow-800">
        <strong>Atenção:</strong> fecha anúncios ML Gold Pro permanentemente. Sem{" "}
        <em>Confirmar execução</em> marcado, roda em dry-run e só exibe o que seria fechado.
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          SKU (opcional — vazio = todos)
        </label>
        <input
          type="text"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          placeholder="EG-FK-XXXX"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={confirm}
          onChange={(e) => setConfirm(e.target.checked)}
          className="rounded accent-red-600"
        />
        <span className={confirm ? "text-red-600 font-semibold" : "text-gray-700"}>
          Confirmar execução (fechar de verdade)
        </span>
      </label>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={excluir}
          onChange={(e) => setExcluir(e.target.checked)}
          className="rounded accent-red-600"
        />
        <span className={excluir ? "text-red-600 font-semibold" : "text-gray-700"}>
          Excluir (em vez de só inativar) — também exclui Premium já fechados do SKU
        </span>
      </label>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className={`font-semibold px-6 py-2 rounded-lg text-sm text-white ${
          confirm
            ? "bg-red-600 hover:bg-red-700 disabled:bg-gray-300"
            : "bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300"
        }`}
      >
        {confirm ? "Fechar Anúncios" : "Simular (dry-run)"}
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

// ─── NVS Categoria ──────────────────────────────────────────────────────────

function NvsCategoryForm() {
  const [scope, setScope]     = useState("sku");
  const [sku, setSku]         = useState("");
  const [skuFile, setSkuFile] = useState("");
  const [dryRun, setDryRun]   = useState(false);
  const [jobId, setJobId]     = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setRunning(true); setJobId(null);
    try {
      const { data } = await runNvsCategory({ ...buildScopeBody(scope, sku, skuFile), dry_run: dryRun });
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Coloca cada produto Funko na categoria raiz de franquia correta na Nuvemshop
        (resolvida a partir da linha do SKU, ex: <code className="bg-gray-100 px-1 rounded">PMOV</code> → Pop Movies).
        Idempotente — pula produto já na categoria certa, mesmo numa subcategoria válida.
      </p>

      <ScopeFields {...{ scope, setScope, sku, setSku, skuFile, setSkuFile }} />

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="rounded" />
        Dry-run (simular sem enviar)
      </label>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        {dryRun ? "Simular" : "Executar"}
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

// ─── NVS Subcategoria ───────────────────────────────────────────────────────

function NvsSubcategoriesForm() {
  const [scope, setScope]     = useState("sku");
  const [sku, setSku]         = useState("");
  const [skuFile, setSkuFile] = useState("");
  const [dryRun, setDryRun]   = useState(false);
  const [jobId, setJobId]     = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setRunning(true); setJobId(null);
    try {
      const { data } = await runNvsSubcategories({ ...buildScopeBody(scope, sku, skuFile), dry_run: dryRun });
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Coloca o produto na subcategoria da Nuvemshop cujo nome aparece no título (ex: título
        com "Jujutsu Kaisen" → subcategoria Jujutsu Kaisen). Personagem sem a franquia no
        título: cadastre em Configurações → Dicionários → nvs_subcategorias.json. Produto sem
        categoria nenhuma recebe a categoria raiz junto. Nunca cria subcategoria nem tira o
        produto de onde ele já está.
      </p>

      <ScopeFields {...{ scope, setScope, sku, setSku, skuFile, setSkuFile }} />

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="rounded" />
        Dry-run (simular sem enviar)
      </label>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        {dryRun ? "Simular" : "Executar"}
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

// ─── NVS — Gerar descrição própria (não envia) ──────────────────────────────

function NvsDescriptionGenerateForm() {
  const [scope, setScope]     = useState("sku");
  const [sku, setSku]         = useState("");
  const [skuFile, setSkuFile] = useState("");
  const [force, setForce]     = useState(false);
  const [jobId, setJobId]     = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setRunning(true); setJobId(null);
    try {
      const { data } = await generateNvsDescription({ ...buildScopeBody(scope, sku, skuFile), force });
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Gera e salva a descrição própria da Nuvemshop (mesma rotina do GDPB). <strong>Não envia</strong> —
        confira o texto no log e depois envie pela ação "Descrição NVS" nos resultados do GVC. Produto que
        já tem descrição da versão atual é pulado. Se a descrição nova não passar na validação, a anterior fica.
      </p>

      <ScopeFields {...{ scope, setScope, sku, setSku, skuFile, setSkuFile }} />

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="rounded" />
        Refazer mesmo se já existir descrição da versão atual
      </label>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        Gerar
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

const TABS = [
  { id: "ml-premium",  label: "Criar ML Premium/Clássico", Form: MlPremiumForm  },
  { id: "ml-price",    label: "ML Preço Mín.",     Form: MlMinPriceForm },
  { id: "amz-price",   label: "Amazon Preço",      Form: AmzPriceForm   },
  { id: "amz-stock",   label: "Amazon Estoque",    Form: AmzStockForm   },
  { id: "shopee-attrs",label: "Shopee Atributos",  Form: ShopeeAttrsForm},
  { id: "sync-images", label: "Sync Imagens",      Form: SyncImagesForm },
  { id: "ml-close",    label: "Fechar MLP",        Form: MlCloseForm    },
  { id: "nvs-category",     label: "Categoria NVS",    Form: NvsCategoryForm      },
  { id: "nvs-subcategories",label: "Subcategoria NVS", Form: NvsSubcategoriesForm },
  { id: "nvs-description-gen", label: "Gerar Descrição NVS", Form: NvsDescriptionGenerateForm },
];

export default function Shared() {
  const [tab, setTab] = useState("ml-premium");
  const active = TABS.find((t) => t.id === tab);

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Shared — Comandos</h2>

      <div className="bg-white rounded-xl shadow">
        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          {TABS.map(({ id, label }) => (
            <button
              type="button"
              key={id}
              onClick={() => setTab(id)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                tab === id
                  ? "border-b-2 border-blue-800 text-blue-800"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Formulário ativo */}
        <div className="p-6">
          {active && <active.Form />}
        </div>
      </div>
    </div>
  );
}
